import { BaseApiServiceResponseHandler } from "../BaseApiServiceResponseHandler";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { shouldTriggerSessionFailureAfterNonOkResponse } from "./httpAuthSessionFailurePolicy";
import type { ProcessResponseOptions } from "../types";

// Wrapper to match the signature expected by BaseApiServiceResponseHandler
async function processResponseWrapper<T>(
	response: Response,
	request: () => Promise<Response>,
	options?: ProcessResponseOptions,
): Promise<T> {
	return processResponse<T>(response, request, options);
}

async function handleNonOkResponse<T>(response: Response, options?: ProcessResponseOptions): Promise<T> {
	const httpStatus = response.status;
	if (shouldTriggerSessionFailureAfterNonOkResponse(httpStatus, options?.skipSessionFailureOnAuthError)) {
		await BaseApiServiceResponseHandler.handleCriticalErrors(httpStatus, response.url);
	}
	return BaseApiServiceResponseHandler.handleErrorResponse(response);
}

function getResponseClone(response: Response): Response | null {
	if (response.bodyUsed) return null;
	try {
		return response.clone();
	} catch (e) {
		LoggerService.warn("⚠️ [BaseApiService] Failed to clone response in processResponse", { error: e });
		return null;
	}
}

interface HandleResponseTextContext {
	responseClone: Response | null;
	contentType: string | null;
	request: () => Promise<Response>;
	options: ProcessResponseOptions;
}

async function handleResponseText<T>(
	text: string,
	response: Response,
	context: HandleResponseTextContext,
): Promise<T> {
	const { responseClone, contentType, request, options } = context;
	if (!text || text.trim().length === 0) {
		const emptyTextResponse = BaseApiServiceResponseHandler.handleEmptyTextResponse<T>(response, contentType);
		if (emptyTextResponse) return emptyTextResponse;
		return BaseApiServiceResponseHandler.handleEmptyResponseBody<T>(
			response,
			request,
			processResponseWrapper,
			options,
		);
	}
	return BaseApiServiceResponseHandler.parseJsonResponse<T>(text, response, responseClone, contentType);
}

export async function processResponse<T>(
	response: Response,
	request: () => Promise<Response>,
	options?: ProcessResponseOptions,
): Promise<T> {
	const opts = options ?? {};
	const { ref } = opts;
	if (ref) ref.response = response;

	if (!response.ok) return handleNonOkResponse<T>(response, opts);

	if (response.status === 304) {
		return BaseApiServiceResponseHandler.handleNotModifiedResponse<T>(response, request, processResponseWrapper, opts);
	}

	const contentType = response.headers.get("Content-Type");
	const blobContent = await BaseApiServiceResponseHandler.handleBlobContent<T>(response, contentType);
	if (blobContent) return blobContent;

	const responseClone = getResponseClone(response);
	const text = await response.text();
	return handleResponseText<T>(text, response, {
		responseClone,
		contentType,
		request,
		options: opts,
	});
}
